import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { useAuth } from '../../../shared/auth/useAuth';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { downloadApontamentosRelatorio } from '../../apontamentos/api';
import { downloadAsPdf } from '../../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../../shared/reports/downloadAsXlsx';
import {
  buildFilename,
  formatDate,
  formatHours,
  formatPeriodo,
} from '../../../shared/reports/formatters';
import { useGerarTimesheet } from '../hooks';
import { RelatorioPageShell } from '../RelatorioPageShell';
import { MesAnoFilter } from '../components/MesAnoFilter';
import type { TimesheetDto } from '../types';

export function EspelhoPontoPage(): JSX.Element {
  const now = new Date();
  const [empregadoId, setEmpregadoId] = useState<number | null>(null);
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const mutation = useGerarTimesheet();
  const { user } = useAuth();
  const toast = useToast();
  const usuario = user?.nome ?? '';
  const [exportingExtrato, setExportingExtrato] = useState(false);

  const result = mutation.data;
  const canGenerate = empregadoId !== null && mes >= 1 && mes <= 12 && ano >= 2000;

  /**
   * Extrato de lançamentos (Excel) do colaborador no mês — o mesmo template da
   * KB (`relApontamentos`, card [kqYokafH]) usado na tela de Apontamentos.
   * Complementa o espelho-resumo com o detalhe dia-a-dia dos apontamentos.
   */
  async function exportExtrato(): Promise<void> {
    if (empregadoId === null) return;
    setExportingExtrato(true);
    try {
      const inicio = dayjs(new Date(ano, mes - 1, 1));
      const blob = await downloadApontamentosRelatorio({
        dataInicial: inicio.format('YYYY-MM-DD'),
        dataFinal: inicio.endOf('month').format('YYYY-MM-DD'),
        colaboradorCodigo: empregadoId,
        clienteCodigo: null,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apontamentos-${ano}-${String(mes).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setExportingExtrato(false);
    }
  }

  function gerar(): void {
    if (!canGenerate || empregadoId === null) return;
    mutation.mutate({ empregadoId, ano, mes });
  }

  function exportPdf(): void {
    if (!result) return;
    const periodo = formatPeriodo(result.cabecalho.mes, result.cabecalho.ano);
    downloadAsPdf(
      buildFilename('espelho-ponto', result.cabecalho.colaboradorNome, periodo),
      {
        title: 'Espelho de Ponto',
        subtitle: `${result.cabecalho.colaboradorNome} — ${periodo}`,
        filters: [
          `Cargo: ${result.cabecalho.cargoNome}`,
          `Carga mensal: ${formatHours(result.cabecalho.cargaMensalHoras)}`,
        ],
        usuario,
      },
      ({ doc, contentStartY }) => {
        autoTable(doc, {
          startY: contentStartY,
          head: [['Data', 'Dia', 'Lançadas', 'Ajustadas', 'Falta', 'Férias', 'Feriado', 'Obs.']],
          body: result.linhas.map((l) => [
            formatDate(l.data),
            l.diaSemana,
            l.horasLancadas,
            l.horasAjustadas,
            l.falta ? 'Sim' : '',
            l.ferias ? 'Sim' : '',
            l.feriado ? 'Sim' : '',
            l.observacao ?? '',
          ]),
          foot: [
            [
              { content: 'Totais', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
              formatHours(result.totais.totalHorasLancadas),
              formatHours(result.totais.totalHorasAjustadas),
              String(result.totais.totalFaltas),
              String(result.totais.totalFerias),
              String(result.totais.totalFeriados),
              `Saldo ${formatHours(result.totais.saldoHoras)}`,
            ],
          ],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [30, 60, 100] },
          footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
        });
      },
    );
  }

  function exportXlsx(): void {
    if (!result) return;
    const periodo = formatPeriodo(result.cabecalho.mes, result.cabecalho.ano);
    downloadAsXlsx(buildFilename('espelho-ponto', result.cabecalho.colaboradorNome, periodo), [
      {
        name: 'Espelho',
        rows: result.linhas.map((l) => ({
          Data: l.data,
          Dia: l.diaSemana,
          Lancadas: l.horasLancadas,
          Ajustadas: l.horasAjustadas,
          Falta: l.falta ? 'Sim' : '',
          Ferias: l.ferias ? 'Sim' : '',
          Feriado: l.feriado ? 'Sim' : '',
          Observacao: l.observacao ?? '',
        })),
        columns: [
          { key: 'Data', header: 'Data', format: 'date', width: 12 },
          { key: 'Dia', header: 'Dia', width: 6 },
          { key: 'Lancadas', header: 'Lançadas', width: 10 },
          { key: 'Ajustadas', header: 'Ajustadas', width: 10 },
          { key: 'Falta', header: 'Falta', width: 8 },
          { key: 'Ferias', header: 'Férias', width: 8 },
          { key: 'Feriado', header: 'Feriado', width: 8 },
          { key: 'Observacao', header: 'Observação', width: 30 },
        ],
      },
      {
        name: 'Resumo',
        rows: [
          { Campo: 'Colaborador', Valor: result.cabecalho.colaboradorNome },
          { Campo: 'Cargo', Valor: result.cabecalho.cargoNome },
          { Campo: 'Período', Valor: periodo },
          { Campo: 'Carga mensal', Valor: result.cabecalho.cargaMensalHoras },
          { Campo: 'Total lançadas', Valor: result.totais.totalHorasLancadas },
          { Campo: 'Total ajustadas', Valor: result.totais.totalHorasAjustadas },
          { Campo: 'Faltas', Valor: result.totais.totalFaltas },
          { Campo: 'Férias', Valor: result.totais.totalFerias },
          { Campo: 'Feriados', Valor: result.totais.totalFeriados },
          { Campo: 'Saldo', Valor: result.totais.saldoHoras },
        ],
        columns: [
          { key: 'Campo', header: 'Campo', width: 22 },
          { key: 'Valor', header: 'Valor', width: 28 },
        ],
      },
    ]);
  }

  return (
    <RelatorioPageShell
      title="Espelho de Ponto"
      subtitle="Horas lançadas e ajustadas por dia, com faltas, férias e feriados."
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ minWidth: 260, flex: 1 }}>
            <ColaboradorSelect
              value={empregadoId}
              onChange={(id) => setEmpregadoId(id)}
              label="Colaborador"
              disabled={mutation.isPending}
            />
          </Box>
          <MesAnoFilter
            mes={mes}
            ano={ano}
            onChange={({ mes: m, ano: a }) => {
              setMes(m);
              setAno(a);
            }}
            disabled={mutation.isPending}
          />
        </Stack>
      }
      canGenerate={canGenerate}
      onGenerate={gerar}
      generating={mutation.isPending}
      hasResult={!!result}
      error={mutation.isError ? mutation.error : null}
      emptyMessage="Escolha colaborador e período e clique em Gerar."
      onExportPdf={exportPdf}
      onExportXlsx={exportXlsx}
      extraActions={
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => void exportExtrato()}
          disabled={exportingExtrato}
        >
          Extrato (Excel)
        </Button>
      }
      preview={result ? <Preview result={result} /> : null}
    />
  );
}

function Preview({ result }: { result: TimesheetDto }): JSX.Element {
  return (
    <>
      <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
        <ResumoItem label="Colaborador" value={result.cabecalho.colaboradorNome} />
        <ResumoItem label="Cargo" value={result.cabecalho.cargoNome} />
        <ResumoItem
          label="Período"
          value={formatPeriodo(result.cabecalho.mes, result.cabecalho.ano)}
        />
        <ResumoItem label="Carga mensal" value={result.cabecalho.cargaMensalHoras} />
      </Stack>

      <TableContainer sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Dia</TableCell>
              <TableCell align="right">Lançadas</TableCell>
              <TableCell align="right">Ajustadas</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell>Observação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.linhas.map((l) => (
              <TableRow key={l.data}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{formatDate(l.data)}</TableCell>
                <TableCell>{l.diaSemana}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {l.horasLancadas}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {l.horasAjustadas}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {l.falta && <Chip size="small" color="error" label="Falta" />}
                    {l.ferias && <Chip size="small" color="info" label="Férias" />}
                    {l.feriado && <Chip size="small" color="default" label="Feriado" />}
                  </Stack>
                </TableCell>
                <TableCell>{l.observacao}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        spacing={3}
        sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
        flexWrap="wrap"
      >
        <ResumoItem label="Lançadas" value={result.totais.totalHorasLancadas} />
        <ResumoItem label="Ajustadas" value={result.totais.totalHorasAjustadas} />
        <ResumoItem label="Saldo" value={result.totais.saldoHoras} />
        <ResumoItem label="Faltas" value={String(result.totais.totalFaltas)} />
        <ResumoItem label="Férias" value={String(result.totais.totalFerias)} />
        <ResumoItem label="Feriados" value={String(result.totais.totalFeriados)} />
      </Stack>
    </>
  );
}

function ResumoItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}
